/**
 * RateLimiter - Client-side rate limiting for authentication requests
 * Prevents brute force attacks and excessive OTP requests
 */

interface RateLimitEntry {
  count: number;
  resetTime: number;
}

class RateLimiter {
  private limits: Map<string, RateLimitEntry> = new Map();

  // Rate limit configurations (requests per time window)
  private readonly LIMITS = {
    'otp-send': { max: 5, window: 15 * 60 * 1000 }, // 5 requests per 15 minutes
    'otp-verify': { max: 10, window: 15 * 60 * 1000 }, // 10 requests per 15 minutes
    'login': { max: 10, window: 15 * 60 * 1000 }, // 10 requests per 15 minutes
    'register': { max: 5, window: 15 * 60 * 1000 }, // 5 requests per 15 minutes
  };

  /**
   * Check if action is allowed
   */
  isAllowed(action: keyof typeof this.LIMITS): boolean {
    const config = this.LIMITS[action];
    const now = Date.now();
    const entry = this.limits.get(action);

    // First request or window expired
    if (!entry || now > entry.resetTime) {
      this.limits.set(action, {
        count: 1,
        resetTime: now + config.window
      });
      return true;
    }

    // Check if limit exceeded
    if (entry.count >= config.max) {
      return false;
    }

    // Increment counter
    entry.count++;
    return true;
  }

  /**
   * Get remaining attempts for an action
   */
  getRemaining(action: keyof typeof this.LIMITS): number {
    const config = this.LIMITS[action];
    const entry = this.limits.get(action);

    if (!entry || Date.now() > entry.resetTime) {
      return config.max;
    }

    return Math.max(0, config.max - entry.count);
  }

  /**
   * Get time until rate limit resets (in seconds)
   */
  getResetTime(action: keyof typeof this.LIMITS): number {
    const entry = this.limits.get(action);

    if (!entry) {
      return 0;
    }

    const timeRemaining = entry.resetTime - Date.now();
    return Math.max(0, Math.ceil(timeRemaining / 1000));
  }

  /**
   * Reset rate limit for an action
   */
  reset(action: keyof typeof this.LIMITS): void {
    this.limits.delete(action);
  }

  /**
   * Reset all rate limits
   */
  resetAll(): void {
    this.limits.clear();
  }

  /**
   * Get error message for rate limit exceeded
   */
  getErrorMessage(action: keyof typeof this.LIMITS): string {
    const resetTime = this.getResetTime(action);
    const minutes = Math.ceil(resetTime / 60);
    
    return `Too many ${action} attempts. Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`;
  }
}

const rateLimiter = new RateLimiter();
export default rateLimiter;
